/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { useEffect, useState } from "react";
import defaultPhotoUrl from "../../assets/svg/generic-profile.svg";
import { useQuery } from "@tanstack/react-query";
import { INTERVAL } from "../../constants/clientTime";
import { useGraphToken } from "../useTokens";

async function getAvatarImageFromGraph(email: string, token: string) {
  const params = {
    method: "GET" as const,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const response = await fetch(
    `https://graph.microsoft.com/v1.0/users/${email}/photo/$value`,
    params
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch avatar (${response.status}): ${response.statusText}`
    );
  }
  const contentType = response.headers.get("Content-Type");
  if (contentType && contentType.includes("application/json")) {
    throw new Error("Expected a Blob but received JSON");
  }
  const blob = await response.blob();
  return blob;
}

function useAvatarImageFromGraph(uid: string) {
  const { data: token, isSuccess } = useGraphToken();
  const result = useQuery({
    queryKey: ["userAvatarFromGraph", uid],
    queryFn: async () => {
      try {
        return await getAvatarImageFromGraph(uid, token!);
      } catch {
        return defaultPhotoUrl;
      }
    },
    enabled: isSuccess && !!uid,
    refetchOnWindowFocus: false,
    staleTime: 1 * INTERVAL.WEEK,
  });
  return result?.data;
}

function useAvatarImagesFromGraph(uids: string[]) {
  const { data: token, isSuccess } = useGraphToken();
  const sortedUids = [...new Set(uids)].sort();

  const result = useQuery({
    queryKey: ["userAvatarsFromGraph", sortedUids],
    queryFn: async () => {
      if (!token) return sortedUids.map(() => defaultPhotoUrl);
      const results = await Promise.all(
        sortedUids.map(async (uid) => {
          try {
            const avatar = await getAvatarImageFromGraph(uid, token);
            return { uid, avatar };
          } catch {
            return { uid, avatar: defaultPhotoUrl };
          }
        })
      );
      return results.reduce<Record<string, Blob | string>>(
        (acc, { uid, avatar }) => {
          acc[uid] = avatar;
          return acc;
        },
        {}
      );
    },
    enabled: isSuccess && uids.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 1 * INTERVAL.WEEK,
  });
  return result?.data;
}

export function useAvatarImage(uid: string) {
  const [avatarUrl, setAvatarUrl] = useState<string>(defaultPhotoUrl);
  const avatarImage = useAvatarImageFromGraph(uid);

  useEffect(() => {
    if (avatarImage instanceof Blob) {
      const objectUrl = URL.createObjectURL(avatarImage);
      setAvatarUrl(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else if (avatarImage) {
      setAvatarUrl(avatarImage);
    }
    return () => {};
  }, [avatarImage]);

  return avatarUrl;
}

export function useAvatarImages(uids: string[]) {
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const avatarImages = useAvatarImagesFromGraph(uids);

  useEffect(() => {
    if (avatarImages) {
      const newAvatarUrls: Record<string, string> = {};
      Object.entries(avatarImages).forEach(([uid, avatarImage]) => {
        if (avatarImage instanceof Blob) {
          const objectUrl = URL.createObjectURL(avatarImage);
          newAvatarUrls[uid] = objectUrl;
        } else {
          newAvatarUrls[uid] = avatarImage;
        }
      });
      setAvatarUrls(newAvatarUrls);
      return () => {
        Object.values(newAvatarUrls).forEach((url) => {
          // Only revoke URLs that were created from Blob (object URLs)
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
      };
    }
    return () => {};
  }, [avatarImages]);

  return avatarUrls;
}

export function useGraphDisplayName(email: string): string | undefined {
  const { data: token, isSuccess } = useGraphToken();

  const { data } = useQuery<string | undefined>({
    queryKey: ["graphDisplayName", email],
    queryFn: async () => {
      const headers = {
        Authorization: `Bearer ${token!}`,
        "Content-Type": "application/json",
      };
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}?$select=displayName`,
        { method: "GET", headers }
      );
      if (!response.ok) return undefined;
      const json = (await response.json()) as { displayName?: string };
      return json.displayName ?? undefined;
    },
    enabled: isSuccess && !!email,
    staleTime: 1 * INTERVAL.WEEK,
    refetchOnWindowFocus: false,
  });

  return data;
}
